import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewAssignmentsApi, aiUseCaseApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { formatDate } from '../utils/format';

interface ReviewAssignment {
  assignmentId: string;
  entityType: string;
  entityId: string;
  scope: 'primary' | 'field';
  fieldKey?: string;
  note?: string;
  status: 'unassigned' | 'assigned' | 'complete';
  assignedByUserId?: string;
  createdAt: string;
  completedAt?: string;
}

// Each entityType a review assignment can point at needs a way to turn its entityId into a
// real link and a human-readable label. ai_use_case is the only real consumer today (this
// module is deliberately generic, not owned by AI use cases - see reviewAssignmentsApi), so
// this stays a lookup table rather than an if/else: a second entityType is a one-line addition.
const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  ai_use_case: (id) => `/ai-use-cases/${id}`,
};
const ENTITY_LABEL: Record<string, string> = {
  ai_use_case: 'AI Use Case',
};
const ENTITY_ICON: Record<string, string> = {
  ai_use_case: 'ri-robot-line',
};

const STATUS_TONE: Record<string, string> = { unassigned: 'neutral', assigned: 'warning', complete: 'success' };
const STATUS_LABEL: Record<string, string> = { unassigned: 'Unassigned', assigned: 'In progress', complete: 'Complete' };

export const GovernanceInboxPage: React.FC = () => {
  const { getUserId } = useAuth();
  const userId = getUserId();
  const queryClient = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const [showCompleted, setShowCompleted] = useState(false);

  const { data, isLoading, error } = useQuery<{ assignments: ReviewAssignment[]; count: number }>({
    queryKey: ['reviewAssignments', 'mine', userId],
    queryFn: () => reviewAssignmentsApi.listForAssignee(userId),
    enabled: !!userId,
  });
  const assignments = useMemo(() => data?.assignments || [], [data]);

  // Resolving entityId -> a title needs each entityType's own register - today that's only
  // AI Use Cases, fetched once and matched client-side, the same resolve-once-for-the-whole-
  // list pattern AiUseCaseList.tsx already uses for department/owner names.
  const { data: useCases = [] } = useQuery({
    queryKey: ['aiUseCases'],
    queryFn: aiUseCaseApi.list,
    enabled: assignments.some((a) => a.entityType === 'ai_use_case'),
  });
  const entityTitle = (a: ReviewAssignment): string => {
    if (a.entityType === 'ai_use_case') {
      return useCases.find((uc) => uc.useCaseId === a.entityId)?.name || a.entityId;
    }
    return a.entityId;
  };

  const completeMutation = useMutation({
    mutationFn: (assignmentId: string) => reviewAssignmentsApi.update(assignmentId, { status: 'complete' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviewAssignments', 'mine', userId] });
      toast.success('Marked complete.');
    },
    onError: (e: Error) => toast.error(`Failed to update: ${e.message}`),
  });

  const visible = useMemo(
    () => assignments
      .filter((a) => showCompleted || a.status !== 'complete')
      .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')),
    [assignments, showCompleted],
  );

  const openCount = assignments.filter((a) => a.status !== 'complete').length;

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-inbox-line"
        title="Governance Inbox"
        subtitle={openCount > 0 ? `${openCount} item${openCount !== 1 ? 's' : ''} need your attention` : 'Nothing outstanding'}
        colour="primary"
        actions={
          <button className="np-btn np-btn-ghost" onClick={() => setShowCompleted((v) => !v)}>
            <i className={showCompleted ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            {showCompleted ? 'Hide completed' : 'Show completed'}
          </button>
        }
      />

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
        </div>
      ) : error ? (
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Failed to load your inbox.</div>
      ) : (
        <NimbusTable
          thead={
            <tr>
              <th>Item</th>
              <th style={{ width: '160px' }}>Type</th>
              <th>Note</th>
              <th style={{ width: '120px' }}>Status</th>
              <th style={{ width: '140px' }}>Assigned</th>
              <th style={{ width: '110px' }}></th>
            </tr>
          }
          isEmpty={visible.length === 0}
          emptyState={
            <div className="text-center py-5">
              <i className="ri-checkbox-circle-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
              <p className="np-sub mb-0">{showCompleted ? 'No assignments yet.' : "You're all caught up."}</p>
            </div>
          }
        >
          {visible.map((a) => {
            const route = ENTITY_ROUTE[a.entityType]?.(a.entityId);
            const label = ENTITY_LABEL[a.entityType] || a.entityType;
            const icon = ENTITY_ICON[a.entityType] || 'ri-file-list-3-line';
            return (
              <tr key={a.assignmentId}>
                <td>
                  {route ? (
                    <Link to={route} className="fw-medium">{entityTitle(a)}</Link>
                  ) : (
                    <span className="fw-medium">{entityTitle(a)}</span>
                  )}
                  {a.scope === 'field' && a.fieldKey && <div className="np-hint">Field: {a.fieldKey}</div>}
                </td>
                <td><span className="np-chip np-tone-neutral"><i className={icon}></i>{label}</span></td>
                <td className="np-sub">{a.note || <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}</td>
                <td><span className={`np-chip np-tone-${STATUS_TONE[a.status] || 'neutral'}`}>{STATUS_LABEL[a.status] || a.status}</span></td>
                <td className="np-tabular">{formatDate(a.createdAt)}</td>
                <td>
                  {a.status !== 'complete' && (
                    <button
                      type="button"
                      className="np-btn np-btn-ghost np-btn-sm"
                      onClick={async () => {
                        const ok = await swal.confirm('Mark this review complete?');
                        if (!ok) return;
                        completeMutation.mutate(a.assignmentId);
                      }}
                      disabled={completeMutation.isPending}
                    >
                      <i className="ri-checkbox-circle-line"></i>Done
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </NimbusTable>
      )}
    </div>
  );
};

export default GovernanceInboxPage;
